<template>
  <a-modal
    :visible="visible"
    :title="modalTitle"
    :body-style="bodyStyle"
    :keyboard="false"
    :mask-closable="false"
    width="600px"
    centered
    wrapClassName="modal-divider edit-field-type-modal"
    @cancel="handleClose"
  >
    <p class="field-type__intro">
      Define si el campo se completará como texto o lista desplegable de opciones.
    </p>

    <span class="field-type__label">Tipo de campo</span>

    <a-radio-group v-model:value="selectedFieldType" class="field-type__options">
      <a-radio
        value="text"
        class="field-type__option"
        :class="{ 'field-type__option--selected': selectedFieldType === 'text' }"
      >
        <div class="field-type__option-content">
          <span class="field-type__option-title">
            <span class="field-type__icon field-type__icon--text">
              <TextIcon />
            </span>
            Texto
          </span>
          <p class="field-type__description">El agente ingresa la información como texto libre.</p>
        </div>
      </a-radio>

      <a-radio
        value="select"
        class="field-type__option"
        :class="{ 'field-type__option--selected': selectedFieldType === 'select' }"
      >
        <div class="field-type__option-content">
          <span class="field-type__option-title">
            <span class="field-type__icon field-type__icon--select">
              <DownOutlined style="font-size: 14px;"/>
            </span>
            Lista desplegable
          </span>
          <p class="field-type__description">
            El agente elige entre las opciones que se configuren.
          </p>
        </div>
      </a-radio>
    </a-radio-group>

    <a-tabs v-if="selectedFieldType === 'select'" v-model:activeKey="activeKey">
      <a-tab-pane key="1" tab="Subir archivo">
        <span style="margin-bottom: 16px; display: block;">
          Haz clic en
          <a-button style="padding: 0; height: 22px;" type="link" @click="handleDownloadTemplate">descargar plantilla</a-button>
          y sube tus opciones.
          Puedes ingresar hasta <strong>2000 opciones</strong>,
          con un <strong>máximo de 100 caracteres</strong> cada una.
        </span>
        <h4><span class="required">*</span>Carga tu archivo aquí</h4>
        <div>
          <archive-picker
            v-model:value="archivePickerValue"
          ></archive-picker>
        </div>
        <a-alert
          v-if="validationReportResponse"
          type="warning"
          show-icon
          style="margin-top: 16px;"
        >
          <template #message>
            Tu archivo tiene errores en una o más celdas.
            <a-button style="padding: 0; height: 22px;" type="link" @click="handleDownloadValidationReport">
              Descargar reporte de errores
            </a-button>
          </template>
        </a-alert>
      </a-tab-pane>
      <a-tab-pane key="2" tab="Manual">
        <a-form ref="form" class="form" :model="formState" layout="vertical">
          <a-form-item
            name="name"
            class="field-type__form-item"
          >
            <template #label>
              <span class="required">*</span>
              Agregar opción
            </template>
            <div class="field-type__input-row">
              <a-input
                v-model:value="formState.name"
                placeholder="Escribe aquí..."
                show-count
                :maxlength="100"
              />
              <a-tooltip :title="addOptionTooltip">
                <a-button
                  :disabled="isAddOptionDisabled"
                  @click="handleAddOption"
                >
                  Agregar
                </a-button>
              </a-tooltip>
            </div>
          </a-form-item>

          <span style="display: inline-flex; gap: 8px; align-items: baseline;">
            <strong style="color:#000000A6">Lista de opciones</strong>
            <span style="color:#00000073; font-size: 12px;">
              {{ options.length }} / 30
            </span>
          </span>

          <a-list
            v-if="options.length"
            :data-source="options"
            :split="false"
            class="field-type__option-list"
          >
            <template #renderItem="{ item, index }">
              <a-list-item>
                <div class="field-type__list-row">
                  <span class="field-type__list-index">
                    {{ index + 1 }}.
                  </span>

                  <div class="field-type__list-content">
                    <span>{{ item }}</span>

                    <a-button
                      type="text"
                      size="small"
                      class="field-type__remove-option"
                      @click="handleRemoveOption(index)"
                    >
                      <CloseOutlined style="font-size: 12px;"/>
                    </a-button>
                  </div>
                </div>
              </a-list-item>
            </template>
          </a-list>
          <a-empty
            v-else
            :image="simpleImage"
            class="field-type__empty"
          >
            <template #description>
              <span class="field-type__empty-description">
                Aún no cuentas con opciones para tu lista desplegable
              </span>
            </template>
          </a-empty>
        </a-form>
      </a-tab-pane>
    </a-tabs>

    <template #footer>
      <div class="field-type__footer">
        <a-button @click="handleClose">Cancelar</a-button>
        <a-tooltip :title="saveButtonTooltip">
          <span class="field-type__save-button">
            <a-button type="primary" :disabled="!hasChanges" @click="handleSave">
              Guardar cambios
            </a-button>
          </span>
        </a-tooltip>
      </div>
    </template>
  </a-modal>
</template>

<script setup lang="ts">
import type { AxiosResponse } from "axios";
import { computed, reactive, ref, watch, createVNode } from "vue";
import { CloseOutlined, DownOutlined, ExclamationCircleOutlined } from "@ant-design/icons-vue";
import { Empty, Modal, message } from 'ant-design-vue';
import { useStore } from "vuex";

import TextIcon from "@/app/imports/components/icons/TextIcon.vue";
import ArchivePicker from "@/app/shared/components/molecules/ArchivePicker.vue";
import { downloadFile } from "@/app/shared/utils/file";

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  field: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(["update:visible", "save"]);
const store = useStore();

const bodyStyle = { minHeight: "534px" };
const selectedFieldType = ref("text");
const activeKey = ref("1");
const initialActiveKey = ref("1");
const formState = reactive({
  name: "",
});
const simpleImage = Empty.PRESENTED_IMAGE_SIMPLE;
const MAX_OPTIONS = 30;
const options = ref<string[]>([]);
const initialOptions = ref<string[]>([]);
const file = ref<File | null>(null);
const existingFileName = ref<string | null>(null);
const validationReportResponse = ref<AxiosResponse<Blob> | null>(null);
const manualOptionsLoaded = ref(false);

type TemplateFieldOptionsValidationResult = {
  isTemplateFieldOptionsValidationFile?: boolean;
  response?: AxiosResponse<Blob>;
};

const initialFieldType = computed(() => props.field?.field_type || "text");
const modalTitle = computed(() => `Editar tipo de campo - ${props.field?.name || ""}`);
const pendingFieldOptions = computed(
  () => store.getters["getPendingTemplateFieldOptions"]?.[props.field?.name],
);
const currentOptionsSource = computed(
  () => pendingFieldOptions.value?.source || props.field?.options_source || null,
);
const archivePickerValue = computed<File | string | null>({
  get: () => file.value || existingFileName.value,
  set: (value) => {
    file.value = value instanceof File ? value : null;

    if (value === null) existingFileName.value = null;
  },
});
const manualOptionsChanged = computed(
  () => JSON.stringify(options.value) !== JSON.stringify(initialOptions.value),
);
const hasChanges = computed(() => {
  if (selectedFieldType.value === "select") {
    if (activeKey.value === "1") return Boolean(file.value);
    if (activeKey.value === "2") {
      if (options.value.length === 0) return false;

      return manualOptionsChanged.value;
    }
  }

  return selectedFieldType.value !== initialFieldType.value;
});
const saveButtonTooltip = computed(() => {
  if (hasChanges.value) return null;

  if (selectedFieldType.value === "select" && activeKey.value === "1" && !Boolean(file.value) && !existingFileName.value) {
    return "Completa el campo requerido para poder guardar.";
  }

  if (selectedFieldType.value === "select" && activeKey.value === "2" && options.value.length === 0) {
    return "Agrega mínimo una opción para poder guardar.";
  }

  return "No hay cambios nuevos por guardar";
});
const addOptionTooltip = computed(() => {
  if (options.value.length >= MAX_OPTIONS) {
    return `Alcanzaste el máximo de opciones.`;
  }

  if (!formState.name.trim()) {
    return "Escribe tu opción para poder agregarla.";
  }

  return null;
});
const isAddOptionDisabled = computed(() => Boolean(addOptionTooltip.value));
const saveConfirmation = computed(() => {
  if (selectedFieldType.value === initialFieldType.value) {
    if (initialActiveKey.value === "1" && activeKey.value === "1") {
      return {
        title: "Estás a punto de actualizar las opciones del campo, ¿deseas continuar?",
        content: createVNode("div", {}, [
          createVNode("strong", {}, "Sucederá lo siguiente:",),
          createVNode("ul", { class: "field-type-confirmation__list" }, [
            createVNode("li", {}, [
              "Al subir un nuevo archivo, se descartarán todas las opciones del archivo anterior. ",
              createVNode("strong", null, "Esta acción no se podrá deshacer."),
            ]),
            createVNode("li", {}, "Si el campo tenía un valor seleccionado, este se conservará hasta que el agente lo edite.",
            ),
          ]),
        ]),
        cancelText: "No, seguir editando",
      };
    }

    if (initialActiveKey.value === "2" && activeKey.value === "2") {
      return {
        title: "Estás a punto de actualizar las opciones del campo, ¿deseas continuar?",
        content: createVNode("div", {}, [
          createVNode("strong", {}, "Sucederá lo siguiente:",),
          createVNode("ul", { class: "field-type-confirmation__list" }, [
            createVNode("li", {}, [
              "Se aplicarán todos los cambios realizados según corresponda. ",
              createVNode("strong", null, "Esta acción no se podrá deshacer."),
            ]),
            createVNode("li", {}, "Si quitaste una opción que el campo tenía como seleccionada, este conservará su valor hasta que el agente lo edite.",
            ),
          ]),
        ]),
        cancelText: "No, seguir editando",
      };
    }

    if (initialActiveKey.value === "2" && activeKey.value === "1") {
      return {
        title: "Estás a punto de actualizar las opciones del campo, ¿deseas continuar?",
        content: createVNode("div", {}, [
          createVNode("strong", {}, "Sucederá lo siguiente:",),
          createVNode("ul", { class: "field-type-confirmation__list" }, [
            createVNode("li", {}, [
              "Al subir este nuevo archivo, se descartarán todas las opciones que fueron agregadas de forma manual. ",
              createVNode("strong", null, "Esta acción no se podrá deshacer."),
            ]),
            createVNode("li", {}, "Si el campo tenía un valor seleccionado, este se conservará hasta que el agente lo edite.",
            ),
          ]),
        ]),
        cancelText: "No, seguir editando",
      };
    }

    return {
      title: "Estás a punto de actualizar las opciones del campo, ¿deseas continuar?",
      content: createVNode("div", {}, [
        createVNode("strong", {}, "Sucederá lo siguiente:",),
        createVNode("ul", { class: "field-type-confirmation__list" }, [
          createVNode("li", {}, [
            "Se descartarán todas las opciones del archivo anteriormente subido. ",
            createVNode("strong", null, "Esta acción no se podrá deshacer."),
          ]),
          createVNode("li", {}, "Si el campo tenía un valor seleccionado, este se conservará hasta que el agente lo edite.",
          ),
        ]),
      ]),
      cancelText: "No, seguir editando",
    };
  }

  if (selectedFieldType.value === "select") {
    return {
      title: "Estás a punto de cambiar este campo a tipo lista desplegable, ¿deseas continuar?",
      content: "Ten en cuenta que si el campo ya tiene un valor completado, este se conservará hasta que el agente lo edite.",
      cancelText: "No, cancelar",
    };
  }

  return {
    title: "Estás a punto de cambiar este campo a tipo texto, ¿deseas continuar?",
    content: createVNode("div", {}, [
      createVNode("strong", {}, "Sucederá lo siguiente:",),
      createVNode("ul", { class: "field-type-confirmation__list" }, [
        createVNode("li", {}, [
          "Todas las opciones configuradas para la lista desplegable se eliminarán. ",
          createVNode("strong", null, "Esta acción no se podrá deshacer."),
        ]),
        createVNode("li", {}, "Si el campo tiene un valor seleccionado, este se conservará pero pasará a ser tipo texto.",
        ),
      ]),
    ]),
    cancelText: "No, cancelar",
  };
});

const handleAddOption = () => {
  const option = formState.name.trim();
  const isPositiveInteger = /^\d+$/;
  const isPositiveNumber = /^\d+(?:\.\d+)?$/;

  if (props.field?.name === "age" && !isPositiveInteger.test(option)) {
    message.error("La opción ingresada no es válida. “Age” solo acepta números enteros.");
    return;
  }

  if (props.field?.name === "salary" && !isPositiveNumber.test(option)) {
    message.error("La opción ingresada no es válida. “Salary” solo acepta números enteros o decimales.");
    return;
  }
  options.value.push(option);
  formState.name = "";
};

const handleRemoveOption = (index: number) => {
  options.value.splice(index, 1);
};

const loadManualOptions = async () => {
  if (manualOptionsLoaded.value || !props.field?.name) return;

  if (currentOptionsSource.value !== "manual") {
    options.value = [];
    initialOptions.value = [];
    manualOptionsLoaded.value = true;
    return;
  }

  try {
    const fieldOptions = await store.dispatch("fetchTemplateFieldOptions", props.field.name);
    const values = (fieldOptions || []).map((option: { value: string }) => option.value);
    options.value = values;
    initialOptions.value = [...values];
    manualOptionsLoaded.value = true;
  } catch {
    message.error("No se pudieron cargar las opciones del campo.");
  }
};

const handleDownloadTemplate = async () => {
  try {
    const template = await store.dispatch("downloadTemplateFieldOptions", props.field.name);
    downloadFile(template, "opciones-campo.xlsx");
  } catch {
    message.error("No se pudo descargar la plantilla.");
  }
};

const handleDownloadValidationReport = () => {
  if (!validationReportResponse.value) return;

  downloadFile(validationReportResponse.value, "validacion_errors.txt", {
    useResponseFileName: true,
  });
};

watch(file, () => {
  validationReportResponse.value = null;
});

watch([activeKey, selectedFieldType], ([tab, fieldType]) => {
  if (props.visible && tab === "2" && fieldType === "select") loadManualOptions();
});

watch(
  () => props.visible,
  (visible) => {
    if (visible) {
      selectedFieldType.value = initialFieldType.value;
      formState.name = "";
      const optionsSource = currentOptionsSource.value;
      const optionsFileName = pendingFieldOptions.value
        ? pendingFieldOptions.value.file_name
        : props.field?.options_file_name;
      activeKey.value = optionsSource === "manual" ? "2" : "1";
      initialActiveKey.value = activeKey.value;
      options.value = [];
      initialOptions.value = [];
      file.value = null;
      existingFileName.value = optionsSource === "upload" ? optionsFileName || null : null;
      validationReportResponse.value = null;
      manualOptionsLoaded.value = false;

      if (pendingFieldOptions.value?.source === "manual") {
        const values = [...pendingFieldOptions.value.options];
        options.value = values;
        initialOptions.value = [...values];
      }

      if (pendingFieldOptions.value) {
        manualOptionsLoaded.value = true;
      }

      if (activeKey.value === "2" && selectedFieldType.value === "select") loadManualOptions();
    }
  }
);

const handleClose = () => {
  Modal.confirm({
    title: "¿Deseas descartar los cambios realizados?",
    content: "Recuerda que si no guardas los cambios que realizaste, estos serán descartados.",
    okText: "Sí, descartar",
    cancelText: "No, seguir editando",
    icon: createVNode(ExclamationCircleOutlined),
    centered: true,
    onOk() {
      emit("update:visible", false);
    }
  })
};

const handleSave = () => {
  if (!hasChanges.value) return;

  Modal.confirm({
    title: saveConfirmation.value.title,
    content: saveConfirmation.value.content,
    okText: "Sí, continuar",
    cancelText: saveConfirmation.value.cancelText,
    icon: createVNode(ExclamationCircleOutlined),
    centered: true,
    async onOk() {

      try {
        if (selectedFieldType.value === "select" && activeKey.value === "1" && file.value) {
          validationReportResponse.value = null;
          const validationResult = await store.dispatch("validateTemplateFieldOptions", {
            fieldName: props.field?.name,
            file: file.value,
          });

          if (validationResult?.isTemplateFieldOptionsValidationFile) {
            validationReportResponse.value = validationResult.response;
            return;
          }

          if (!Array.isArray(validationResult?.options)) {
            throw new Error("La validacion no devolvio las opciones del archivo.");
          }

          await store.dispatch("setPendingTemplateFieldOptions", {
            fieldName: props.field?.name,
            options: validationResult.options,
            source: "upload",
            file_name: validationResult.file_name,
          });
        }

        if (selectedFieldType.value === "select" && activeKey.value === "2") {
          await store.dispatch("setPendingTemplateFieldOptions", {
            fieldName: props.field?.name,
            options: options.value,
            source: "manual",
          });
        }

        if (selectedFieldType.value === "text") {
          await store.dispatch("removePendingTemplateFieldOptions", props.field?.name);
        }

        emit("save", {
          ...props.field,
          field_type: selectedFieldType.value,
        });
        emit("update:visible", false);
        message.success("Cambios guardados con éxito.")
      } catch (error) {
        const validationError = error as TemplateFieldOptionsValidationResult;

        if (validationError?.isTemplateFieldOptionsValidationFile && validationError.response) {
          validationReportResponse.value = validationError.response;
          return;
        }

        message.error("Corrige los errores en tu plantilla y vuelve a cargarla.");
      }
    }
  })
};
</script>

<style lang="sass" scoped>
.field-type
  &__intro
    margin-bottom: 16px

  &__label
    display: block
    margin-bottom: 8px
    font-weight: 700

  &__options
    display: grid
    grid-template-columns: repeat(2, minmax(0, 1fr))
    gap: 16px

  &__option
    position: relative
    display: block
    height: 96px
    margin-inline-end: 0
    padding: 12px
    border: $border-normal
    border-radius: 8px
    cursor: pointer
    outline: none
    transition: border-color 0.2s, background-color 0.2s

    &:hover,
    &:focus-visible
      border-color: $blue-5

    &--selected
      border-color: $primary
      background-color: $blue-1

    :deep(.ant-radio)
      position: absolute
      top: 12px
      right: 12px

    :deep(.ant-radio + span)
      display: block
      padding-inline: 0

  &__option-content
    display: block

  &__option-title
    display: inline-flex
    align-items: center
    gap: 8px
    margin-bottom: 8px

  &__option-list
    margin-top: 8px
    max-height: 172px
    overflow-y: auto

    :deep(.ant-list-item)
      padding: 4px 0
      border-block-end: 0

  &__list-row
    display: grid
    grid-template-columns: 24px minmax(0, 1fr)
    align-items: center
    width: 100%
    height: 22px

  &__list-index
    font-size: 12px

  &__list-content
    display: flex
    align-items: center
    justify-content: space-between
    padding-left: 8px
    background-color: #F5F5F5
    font-size: 12px
    border-radius: 4px

  &__remove-option
    color: #00000073

  &__icon
    display: inline-flex
    align-items: center
    justify-content: center
    width: 22px
    height: 22px
    border: 1px solid
    border-radius: 4px

    &--text
      color: #AD6800
      background-color: #FFFBE6
      border-color: #FFE58F

    &--select
      color: #C41D7F
      background-color: #FFF0F6
      border-color: #FFADD2

  &__description
    margin: 0
    color: #000000A6
    font-size: 12px
    line-height: 20px

  &__footer
    display: flex
    justify-content: flex-end
    gap: 8px

  &__save-button
    display: inline-block

  &__input-row
    display: flex
    gap: 8px

  &__empty
    display: flex
    flex-direction: column
    justify-content: center
    height: 100px
    margin: 12px 0 56px
    background-color: #00000005
    border-radius: 8px

  &__empty-description
    font-size: 12px

  &__form-item
    margin-bottom: 16px

.required
  color: $red-7
  margin-right: 4px
</style>

<style>
.edit-field-type-modal .ant-modal-body {
  padding: 16px 24px !important;
}

.edit-field-type-modal .ant-modal-footer {
  padding: 16px 24px !important;
}

.field-type-confirmation__list {
  padding-left: 20px;
}

</style>
